// src/pages/tienda/MiCuenta.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Breadcrumb from '../../components/Breadcrumb';
import toast from 'react-hot-toast';

const fmtCOP = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);

const ESTADO_BADGE = {
  nuevo:      { label:'Nuevo',       color:'#185fa5', bg:'#e6f1fb' },
  preparando: { label:'Preparando',  color:'#8a6000', bg:'#fff8e1' },
  en_proceso: { label:'En proceso',  color:'#8a6000', bg:'#fff8e1' },
  enviado:    { label:'Enviado',     color:'#2c5e2e', bg:'#e8f3e6' },
  entregado:  { label:'Entregado',   color:'#2c5e2e', bg:'#e8f3e6' },
  cancelado:  { label:'Cancelado',   color:'#a32d2d', bg:'#fcebeb' },
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export default function MiCuenta() {
  const navigate = useNavigate();
  const token = localStorage.getItem('wawita_cliente_token');
  const [vista, setVista] = useState(token ? 'cuenta' : 'login');
  const [usuario, setUsuario] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [loginForm, setLoginForm] = useState({ email:'', password:'' });
  const [regForm, setRegForm] = useState({ nombre:'', email:'', password:'', telefono:'' });
  const [perfForm, setPerfForm] = useState({ nombre:'', telefono:'' });
  const [tab, setTab] = useState('pedidos');
  const [loading, setLoading] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [aceptaPrivacidad, setAceptaPrivacidad] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleToken = params.get('token');
    const googleError = params.get('error');
    if (googleToken) {
      localStorage.setItem('wawita_cliente_token', googleToken);
      window.history.replaceState({}, '', '/mi-cuenta');
      cargarCuenta(googleToken);
      return;
    }
    if (googleError) {
      toast.error('Error al iniciar sesion con Google');
      window.history.replaceState({}, '', '/mi-cuenta');
    }
    if (token) cargarCuenta(token);
  }, []);

  const cargarCuenta = async (tk) => {
    const t = tk || localStorage.getItem('wawita_cliente_token');
    try {
      const r = await api.get('/auth/perfil', { headers: { Authorization: `Bearer ${t}` } });
      setUsuario(r.data);
      setPerfForm({ nombre: r.data.nombre, telefono: r.data.telefono || '' });
      const p = await api.get('/auth/mis-pedidos', { headers: { Authorization: `Bearer ${t}` } });
      setPedidos(p.data);
      setVista('cuenta');
    } catch {
      localStorage.removeItem('wawita_cliente_token');
      setVista('login');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/login', loginForm);
      localStorage.setItem('wawita_cliente_token', r.data.token);
      toast.success('Bienvenida, ' + r.data.usuario.nombre + '! 🌿');
      await cargarCuenta(r.data.token);
    } catch { toast.error('Correo o contrasena incorrectos'); }
    finally { setLoading(false); }
  };

  const handleRegistro = async (e) => {
    e.preventDefault();
    if (!regForm.nombre || !regForm.email || !regForm.password) return toast.error('Completa todos los campos');
    if (!aceptaTerminos || !aceptaPrivacidad) return toast.error('Debes aceptar los terminos y la politica de privacidad');
    setLoading(true);
    try {
      const r = await api.post('/auth/registro-cliente', regForm);
      localStorage.setItem('wawita_cliente_token', r.data.token);
      toast.success('Cuenta creada exitosamente! 🌿');
      await cargarCuenta(r.data.token);
    } catch(err) { toast.error(err.response?.data?.error || 'Error al crear cuenta'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error('Ingresa tu correo');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success('Si el correo existe, recibiras instrucciones');
      setShowForgot(false);
      setForgotEmail('');
    } catch { toast.error('Error al enviar correo'); }
    finally { setLoading(false); }
  };

  const actualizarPerfil = async (e) => {
    e.preventDefault();
    try {
      await api.put('/auth/perfil', perfForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Perfil actualizado');
      setUsuario(u => ({ ...u, ...perfForm }));
    } catch { toast.error('Error al actualizar'); }
  };

  const cerrarSesion = () => {
    localStorage.removeItem('wawita_cliente_token');
    setUsuario(null);
    setPedidos([]);
    setVista('login');
    toast.success('Sesion cerrada');
  };

  const Divider = () => (
    <div style={{ display:'flex', alignItems:'center', gap:10, margin:'20px 0' }}>
      <div style={{ flex:1, height:1, background:'rgba(28,58,30,0.1)' }} />
      <span style={{ fontSize:12, color:'var(--ink-faint)', fontWeight:600 }}>o continua con</span>
      <div style={{ flex:1, height:1, background:'rgba(28,58,30,0.1)' }} />
    </div>
  );

  const GoogleBtn = ({ texto }) => (
    <a href="/api/auth/google" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%', padding:'11px', border:'1.5px solid rgba(28,58,30,0.15)', borderRadius:'var(--r-md)', background:'#fff', color:'var(--ink)', fontWeight:700, fontSize:14, textDecoration:'none', transition:'all 0.2s', boxSizing:'border-box' }}>
      <GoogleIcon /> {texto}
    </a>
  );

  if (vista === 'login' || vista === 'registro') return (
    <div>
      <Breadcrumb items={[{ label:'Wawita', href:'/' }, { label:'Mi cuenta' }]} />
      <div style={{ maxWidth:480, margin:'60px auto', padding:'0 24px' }}>

        <div style={{ display:'flex', background:'rgba(28,58,30,0.06)', borderRadius:12, padding:4, marginBottom:28 }}>
          {[['login','Iniciar sesion'],['registro','Crear cuenta']].map(([v,l]) => (
            <button key={v} onClick={() => setVista(v)} style={{ flex:1, padding:'10px', borderRadius:9, border:'none', fontFamily:'var(--font-body)', fontWeight:700, fontSize:14, cursor:'pointer', background: vista===v?'#fff':'transparent', color: vista===v?'var(--forest)':'var(--ink-faint)', transition:'all 0.15s' }}>{l}</button>
          ))}
        </div>

        <div className="card">
          <div className="card-body">

            {vista === 'login' ? (
              <>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, color:'var(--forest)', marginBottom:24 }}>Bienvenida de nuevo</h2>
                <form onSubmit={handleLogin}>
                  <div className="form-group">
                    <label className="form-label">Correo electronico</label>
                    <input className="form-input" type="email" placeholder="tu@email.com" value={loginForm.email} onChange={e=>setLoginForm(f=>({...f,email:e.target.value}))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contrasena</label>
                    <input className="form-input" type="password" placeholder="••••••••" value={loginForm.password} onChange={e=>setLoginForm(f=>({...f,password:e.target.value}))} required />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width:'100%', padding:13, fontSize:15, marginTop:8, background:'#9FBE6A', color:'#1C3A1E' }} disabled={loading}>
                    {loading ? 'Ingresando...' : 'Iniciar sesion'}
                  </button>
                </form>

                <Divider />
                <GoogleBtn texto="Continuar con Google" />

                <div style={{ marginTop:16, textAlign:'center' }}>
                  {!showForgot ? (
                    <p style={{ fontSize:13, color:'var(--ink-faint)', margin:0 }}>
                      <button onClick={() => setShowForgot(true)} style={{ background:'none', border:'none', color:'var(--forest)', fontWeight:600, cursor:'pointer', fontSize:13 }}>Olvide mi contrasena</button>
                      {' · '}
                      <button onClick={() => setVista('registro')} style={{ background:'none', border:'none', color:'var(--forest)', fontWeight:700, cursor:'pointer', fontSize:13 }}>Registrate gratis</button>
                    </p>
                  ) : (
                    <div style={{ background:'rgba(159,190,106,0.1)', borderRadius:10, padding:16, textAlign:'left' }}>
                      <p style={{ fontSize:13, color:'var(--forest)', fontWeight:700, marginBottom:10, margin:'0 0 10px' }}>Recuperar contrasena</p>
                      <form onSubmit={handleForgotPassword} style={{ display:'flex', gap:8 }}>
                        <input className="form-input" type="email" placeholder="tu@email.com" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} style={{ flex:1, fontSize:13 }} />
                        <button type="submit" className="btn btn-outline btn-sm" disabled={loading}>{loading ? '...' : 'Enviar'}</button>
                      </form>
                      <button onClick={() => setShowForgot(false)} style={{ background:'none', border:'none', color:'var(--ink-faint)', cursor:'pointer', fontSize:12, marginTop:8 }}>Cancelar</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, color:'var(--forest)', marginBottom:24 }}>Crear cuenta</h2>
                <form onSubmit={handleRegistro}>
                  {[
                    { key:'nombre', label:'Nombre completo *', ph:'Maria Garcia', type:'text' },
                    { key:'email', label:'Correo electronico *', ph:'tu@email.com', type:'email' },
                    { key:'telefono', label:'Telefono (opcional)', ph:'+57 300...', type:'tel' },
                    { key:'password', label:'Contrasena *', ph:'Minimo 8 caracteres', type:'password' },
                  ].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" type={f.type} placeholder={f.ph} value={regForm[f.key]} onChange={e=>setRegForm(x=>({...x,[f.key]:e.target.value}))} />
                    </div>
                  ))}

                  <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:16 }}>
                    <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', fontSize:13, color:'var(--ink-mid)', lineHeight:1.5 }}>
                      <input type="checkbox" checked={aceptaTerminos} onChange={e=>setAceptaTerminos(e.target.checked)} style={{ marginTop:2, accentColor:'#9FBE6A', flexShrink:0 }} />
                      <span>He leido y acepto los <Link to="/terminos" target="_blank" style={{ color:'var(--forest)', fontWeight:700 }}>Terminos y Condiciones</Link></span>
                    </label>
                    <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', fontSize:13, color:'var(--ink-mid)', lineHeight:1.5 }}>
                      <input type="checkbox" checked={aceptaPrivacidad} onChange={e=>setAceptaPrivacidad(e.target.checked)} style={{ marginTop:2, accentColor:'#9FBE6A', flexShrink:0 }} />
                      <span>Autorizo el tratamiento de mis datos conforme a la <Link to="/privacidad" target="_blank" style={{ color:'var(--forest)', fontWeight:700 }}>Politica de Privacidad</Link> (Ley 1581 de 2012)</span>
                    </label>
                  </div>

                  <button type="submit" style={{ width:'100%', padding:13, fontSize:15, marginTop:16, background: aceptaTerminos && aceptaPrivacidad ? '#9FBE6A' : '#ccc', color: aceptaTerminos && aceptaPrivacidad ? '#1C3A1E' : '#fff', border:'none', borderRadius:'var(--r-md)', fontWeight:700, cursor: aceptaTerminos && aceptaPrivacidad ? 'pointer' : 'not-allowed', fontFamily:'var(--font-body)' }} disabled={loading || !aceptaTerminos || !aceptaPrivacidad}>
                    {loading ? 'Creando cuenta...' : 'Crear mi cuenta'}
                  </button>
                </form>

                <Divider />
                <GoogleBtn texto="Registrarse con Google" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Breadcrumb items={[{ label:'Wawita', href:'/' }, { label:'Mi cuenta' }]} />
      <div style={{ maxWidth:1000, margin:'0 auto', padding:'48px 24px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:32 }}>
          <div>
            <h1 style={{ fontFamily:'var(--font-display)', fontSize:32, color:'var(--forest)', marginBottom:4 }}>Hola, {usuario?.nombre} 🌿</h1>
            <p style={{ fontSize:14, color:'var(--ink-faint)' }}>{usuario?.email}</p>
          </div>
          <button onClick={cerrarSesion} style={{ padding:'8px 20px', background:'#fcebeb', color:'#a32d2d', border:'none', borderRadius:10, fontFamily:'var(--font-body)', fontWeight:700, cursor:'pointer', fontSize:13 }}>Cerrar sesion</button>
        </div>

        <div style={{ display:'flex', gap:4, background:'rgba(28,58,30,0.06)', borderRadius:12, padding:4, marginBottom:28, width:'fit-content' }}>
          {[['pedidos','Mis pedidos'],['perfil','Mi perfil']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ padding:'10px 24px', borderRadius:9, border:'none', fontFamily:'var(--font-body)', fontWeight:700, fontSize:14, cursor:'pointer', background: tab===t?'#fff':'transparent', color: tab===t?'var(--forest)':'var(--ink-faint)', transition:'all 0.15s' }}>{l}</button>
          ))}
        </div>

        {tab === 'pedidos' && (
          <div>
            {pedidos.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:'var(--ink-faint)' }}>
                <div style={{ fontSize:56, marginBottom:16 }}>📦</div>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--forest)', marginBottom:8 }}>Aun no tienes pedidos</h3>
                <Link to="/" className="btn btn-primary" style={{ marginTop:12, background:'#9FBE6A', color:'#1C3A1E' }}>Ver productos</Link>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {pedidos.map(p => {
                  const est = ESTADO_BADGE[p.estado] || { label:p.estado, color:'#666', bg:'#f0ede6' };
                  return (
                    <div key={p.id} className="card">
                      <div className="card-body">
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
                              <span style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:17, color:'var(--forest)' }}>{p.numero}</span>
                              <span style={{ padding:'3px 12px', borderRadius:20, fontSize:12, fontWeight:700, background:est.bg, color:est.color }}>{est.label}</span>
                            </div>
                            <div style={{ fontSize:13, color:'var(--ink-faint)' }}>{new Date(p.created_at).toLocaleDateString('es-CO', { year:'numeric', month:'long', day:'numeric' })}</div>
                            {p.ciudad_entrega && <div style={{ fontSize:13, color:'var(--ink-faint)', marginTop:2 }}>Entrega en: {p.ciudad_entrega}</div>}
                            {p.numero_guia && <div style={{ fontSize:13, color:'var(--sage)', fontWeight:600, marginTop:4 }}>Guia: {p.transportadora} · {p.numero_guia}</div>}
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:22, color:'var(--forest)' }}>{fmtCOP(p.total)}</div>
                            <div style={{ fontSize:12, color:'var(--ink-faint)', marginTop:2 }}>{p.metodo_pago}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'perfil' && (
          <div className="card" style={{ maxWidth:500 }}>
            <div className="card-header"><span className="card-title">Mis datos</span></div>
            <div className="card-body">
              <form onSubmit={actualizarPerfil}>
                <div className="form-group">
                  <label className="form-label">Nombre completo</label>
                  <input className="form-input" value={perfForm.nombre} onChange={e=>setPerfForm(f=>({...f,nombre:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Correo electronico</label>
                  <input className="form-input" value={usuario?.email} disabled style={{ background:'var(--cream-dark)', color:'var(--ink-faint)' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono / WhatsApp</label>
                  <input className="form-input" value={perfForm.telefono} onChange={e=>setPerfForm(f=>({...f,telefono:e.target.value}))} placeholder="+57 300..." />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width:'100%', padding:12, background:'#9FBE6A', color:'#1C3A1E' }}>Guardar cambios</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
